/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { ClosedEnum } from "../../types/enums.js";

/**
 * The network surfaces through which a Speakeasy-hosted MCP server may be reached.
 */
export const UpdateMcpServerFormNetworkAccessMode = {
  PublicOnly: "public_only",
  Dual: "dual",
  PrivateOnly: "private_only",
} as const;
/**
 * The network surfaces through which a Speakeasy-hosted MCP server may be reached.
 */
export type UpdateMcpServerFormNetworkAccessMode = ClosedEnum<
  typeof UpdateMcpServerFormNetworkAccessMode
>;

/**
 * The visibility of an MCP server
 */
export const UpdateMcpServerFormVisibility = {
  Disabled: "disabled",
  Private: "private",
  Public: "public",
} as const;
/**
 * The visibility of an MCP server
 */
export type UpdateMcpServerFormVisibility = ClosedEnum<
  typeof UpdateMcpServerFormVisibility
>;

/**
 * Form for updating an MCP server. This is a full-record replace for backend references: fields omitted from the request become null on the stored record. Exactly one of remote_mcp_server_id, tunneled_mcp_server_id, toolset_id, or unproxied_mcp_server_id must be provided. Omit name or user_session_issuer_id to preserve the existing value; the slug is recomputed server-side from the resulting name.
 */
export type UpdateMcpServerForm = {
  /**
   * The ID of the environment to associate with the server
   */
  environmentId?: string | undefined;
  /**
   * The ID of the MCP server to update
   */
  id: string;
  /**
   * A human-readable display name for the server. Omit to leave the existing name unchanged; if provided, must be non-empty.
   */
  name?: string | undefined;
  /**
   * The network surfaces through which a Speakeasy-hosted MCP server may be reached.
   */
  networkAccessMode?: UpdateMcpServerFormNetworkAccessMode | undefined;
  /**
   * The ID of the remote MCP server to use as the backend
   */
  remoteMcpServerId?: string | undefined;
  /**
   * The ID of the tool variations group enabling MCP tool filtering for this server. Omit to disable filtering (cleared to null, consistent with the full-record replace semantics of the other UUID references).
   */
  toolVariationsGroupId?: string | undefined;
  /**
   * The ID of the toolset to use as the backend
   */
  toolsetId?: string | undefined;
  /**
   * The ID of the tunneled MCP server to use as the backend
   */
  tunneledMcpServerId?: string | undefined;
  /**
   * The ID of the unproxied MCP server to use as the backend
   */
  unproxiedMcpServerId?: string | undefined;
  /**
   * The ID of an existing project- or organization-owned user session issuer to attach. Omit to preserve the current issuer.
   */
  userSessionIssuerId?: string | undefined;
  /**
   * The visibility of an MCP server
   */
  visibility: UpdateMcpServerFormVisibility;
};

/** @internal */
export const UpdateMcpServerFormNetworkAccessMode$outboundSchema: z.ZodMiniEnum<
  typeof UpdateMcpServerFormNetworkAccessMode
> = z.enum(UpdateMcpServerFormNetworkAccessMode);

/** @internal */
export const UpdateMcpServerFormVisibility$outboundSchema: z.ZodMiniEnum<
  typeof UpdateMcpServerFormVisibility
> = z.enum(UpdateMcpServerFormVisibility);

/** @internal */
export type UpdateMcpServerForm$Outbound = {
  environment_id?: string | undefined;
  id: string;
  name?: string | undefined;
  network_access_mode?: string | undefined;
  remote_mcp_server_id?: string | undefined;
  tool_variations_group_id?: string | undefined;
  toolset_id?: string | undefined;
  tunneled_mcp_server_id?: string | undefined;
  unproxied_mcp_server_id?: string | undefined;
  user_session_issuer_id?: string | undefined;
  visibility: string;
};

/** @internal */
export const UpdateMcpServerForm$outboundSchema: z.ZodMiniType<
  UpdateMcpServerForm$Outbound,
  UpdateMcpServerForm
> = z.pipe(
  z.object({
    environmentId: z.optional(z.string()),
    id: z.string(),
    name: z.optional(z.string()),
    networkAccessMode: z.optional(
      UpdateMcpServerFormNetworkAccessMode$outboundSchema,
    ),
    remoteMcpServerId: z.optional(z.string()),
    toolVariationsGroupId: z.optional(z.string()),
    toolsetId: z.optional(z.string()),
    tunneledMcpServerId: z.optional(z.string()),
    unproxiedMcpServerId: z.optional(z.string()),
    userSessionIssuerId: z.optional(z.string()),
    visibility: UpdateMcpServerFormVisibility$outboundSchema,
  }),
  z.transform((v) => {
    return remap$(v, {
      environmentId: "environment_id",
      networkAccessMode: "network_access_mode",
      remoteMcpServerId: "remote_mcp_server_id",
      toolVariationsGroupId: "tool_variations_group_id",
      toolsetId: "toolset_id",
      tunneledMcpServerId: "tunneled_mcp_server_id",
      unproxiedMcpServerId: "unproxied_mcp_server_id",
      userSessionIssuerId: "user_session_issuer_id",
    });
  }),
);

export function updateMcpServerFormToJSON(
  updateMcpServerForm: UpdateMcpServerForm,
): string {
  return JSON.stringify(
    UpdateMcpServerForm$outboundSchema.parse(updateMcpServerForm),
  );
}
